<template>
    <Dropdown
        class="order-time-dropdown"
        v-model="selectedTime"
        panel-class="order-time-dropdown-panel preorder-time-slots-panel scrollable-element"
        :options="currentOptions"
        optionLabel="label"
        optionValue="value"
        :disabled="props.disabled"
        scrollHeight="300px"
    />
</template>

<script setup lang="ts">
    import type { IFulfillmentTimeSlotDto } from "@/data/model/Customer";
    import { computed, ref, watch } from "vue";

    import Dropdown from "primevue/dropdown";

    // define props
    const props = defineProps<{
        modelValue: any | null;
        options: IFulfillmentTimeSlotDto[] | undefined;
        disabled: boolean;
        inputId?: string;
    }>();

    // define emits
    const emit = defineEmits(["update:modelValue"]);

    // define state
    const selectedTime = ref(props.modelValue);

    // define computed
    const currentOptions = computed(() => {
        return (
            props.options?.map((x) => {
                return {
                    label: x.startTime.substring(0, 5),
                    value: x.startTime,
                };
            }) ?? []
        );
    });

    // event handlers
    watch(selectedTime, (newSelectedTime) => {
        emit("update:modelValue", newSelectedTime);
    });

    // update watchers
    watch(
        () => props.modelValue,
        (newSelectedTime) => {
            selectedTime.value = newSelectedTime;
        }
    );
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .order-time-dropdown {
        text-align: left;
        color: colors.$fontColor;
        min-width: 100px;
        width: 300px;
        border-radius: 4px;
        box-shadow: none !important;

        display: inline-flex;
        cursor: pointer;
        position: relative;
        user-select: none;

        .p-dropdown-label {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            flex: 1 1 auto;
            width: 1%;
            text-overflow: ellipsis;
            cursor: pointer;
        }

        .p-dropdown-trigger {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }

        > .p-inputtext {
            padding: 0.75em;
            font-size: 18px !important;
        }
    }

    .order-time-dropdown-panel {
        .p-dropdown-items {
            .p-dropdown-item {
                color: colors.$fontColor;
                border-bottom: 1px solid colors.$greyBorder !important;
                padding: 0.75em 1em;

                &:last-child {
                    border-bottom: none !important;
                }
            }
        }
    }

    // kiosk: the control is sized in px, so its width, its text and the corner radius scale
    // together. the label padding is in em and follows its font size
    .kiosk-mode .order-time-dropdown {
        min-width: 100px * variables.$kioskScale;
        width: 300px * variables.$kioskScale;
        border-radius: 4px * variables.$kioskScale;

        > .p-inputtext {
            font-size: 18px * variables.$kioskScale !important;
        }

        // the chevron box and the icon in it are the primevue defaults, both in rem, so
        // neither of them follows the text next to it
        .p-dropdown-trigger {
            width: 2.357rem * variables.$kioskScale;

            .p-icon {
                width: 1rem * variables.$kioskScale;
                height: 1rem * variables.$kioskScale;
            }
        }
    }

    // the panel hangs off the body. its items are padded in em, so their font size carries
    // the row height with it; the list padding around them is the primevue default in rem.
    // scoped on the second panel class, because .order-time-dropdown-panel is also worn by
    // the country dropdown of the pay flow, which has no kiosk size of its own
    .kiosk-mode .preorder-time-slots-panel {
        .p-dropdown-items {
            padding: (0.5rem * variables.$kioskScale) 0;

            .p-dropdown-item {
                font-size: 1rem * variables.$kioskScale;
            }
        }

        // the scroll height is an inline max-height from the scrollHeight prop, so it takes
        // an !important to grow with the rows and keep the same number of times in view
        .p-dropdown-items-wrapper {
            max-height: 300px * variables.$kioskScale !important;
        }
    }
</style>
